/**
 * prayerConfirmations.js
 *
 * Local, offline-only state manager for the "prayer performed" confirmations.
 * Data lives entirely in localStorage — nothing is sent to any server.
 *
 * Storage schema (key: `falak.prayer.confirmed.v1`):
 * {
 *   date: 'YYYY-MM-DD',                  // local calendar date the entries belong to
 *   prayers: {                            // ISO string of the moment the user confirmed
 *     fajr:    '2026-07-23T05:31:22.000Z',
 *     dhuhr:   '2026-07-23T12:45:03.000Z',
 *     ...
 *   }
 * }
 *
 * At every read, if the stored `date` differs from today (i.e. local midnight
 * has passed), the record is silently wiped so a fresh cycle begins with the
 * next Fajr.
 *
 * Confirmation rules:
 *   • A prayer can only be confirmed AFTER its scheduled time has entered
 *     PLUS a small delay (`CONFIRM_DELAY_MS`, default 10 seconds). This
 *     eliminates accidental taps right at prayer time.
 *   • Once confirmed, a prayer CANNOT be un-confirmed. The one-way state
 *     machine removes doubt by design.
 */

const STORAGE_KEY = 'falak.prayer.confirmed.v1';
export const CONFIRM_DELAY_MS = 10_000;

function todayKey() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function readRaw() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

function writeRaw(obj) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch (_) { /* ignore quota / unavailable storage */ }
}

/**
 * Returns today's confirmations, auto-wiping the record if the calendar day
 * has changed since it was last written.
 */
export function getTodayConfirmations() {
  const rec = readRaw();
  const today = todayKey();
  if (!rec || rec.date !== today) {
    // Day rolled over — start a fresh cycle.
    if (rec) writeRaw({ date: today, prayers: {} });
    return {};
  }
  return rec.prayers || {};
}

/**
 * Whether a given prayer has been confirmed today.
 */
export function isConfirmed(prayer) {
  const c = getTodayConfirmations();
  return Boolean(c[prayer]);
}

/**
 * Returns the ISO timestamp at which the given prayer was confirmed today,
 * or `null` if not confirmed.
 */
export function confirmedAt(prayer) {
  const c = getTodayConfirmations();
  return c[prayer] || null;
}

/**
 * Whether the given prayer is currently eligible for confirmation.
 * A prayer is eligible when:
 *   • It has not already been confirmed today.
 *   • `now` >= prayerTime + CONFIRM_DELAY_MS.
 */
export function canConfirm(prayer, prayerTimeIso, nowMs = Date.now()) {
  if (!prayerTimeIso) return false;
  if (isConfirmed(prayer)) return false;
  const ts = new Date(prayerTimeIso).getTime();
  if (Number.isNaN(ts)) return false;
  return nowMs >= ts + CONFIRM_DELAY_MS;
}

/**
 * Confirms the given prayer. No-op if already confirmed (one-way state).
 * Returns the ISO timestamp of the confirmation (or the existing one).
 */
export function confirmPrayer(prayer) {
  const rec = readRaw();
  const today = todayKey();
  const prayers = rec && rec.date === today ? { ...(rec.prayers || {}) } : {};
  if (prayers[prayer]) return prayers[prayer]; // already confirmed — do nothing
  const nowIso = new Date().toISOString();
  prayers[prayer] = nowIso;
  writeRaw({ date: today, prayers });
  return nowIso;
}
